import { Component } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'

import { SbTabsComponent } from './sb-tabs.component'

interface SbLanguageSwitcherScope {
  languageSectionVisible: boolean
  tabVisibilityConditions: Record<string, boolean>
  isSubmitting: boolean
  availableLanguagesPerMarket: Record<string, any[]>
}

interface ShopLocale {
  iso_code: string
  primary: boolean
  root_url: string
  endonym_name: string
  name: string
}

interface LanguageObject {
  shop_locale: ShopLocale
}

export class SbLanguageSwitcherComponent extends Component {
  public static tagName = 'sb-language-switcher'
  protected autobind = true

  static get observedAttributes(): string[] {
    return ['available-languages-per-market']
  }

  protected formEl: HTMLFormElement | null = null
  protected countrySectionEl: HTMLElement | null = null
  protected countryInputEl: NodeListOf<HTMLInputElement> | null = null
  protected countryLabelEl: NodeListOf<HTMLInputElement> | null = null
  protected languageSectionEl: HTMLElement | null = null
  protected languageInputEl: NodeListOf<HTMLInputElement> | null = null
  protected languageLabelEl: NodeListOf<HTMLInputElement> | null = null
  protected redirectMessageEl: HTMLElement | null = null
  protected selectedCountryName: string = ''
  protected selectedLanguageName: string = ''
  protected selectedLanguageSpan: HTMLElement | null = null
  // Use the updated interface name
  public scope: SbLanguageSwitcherScope = {
    languageSectionVisible: false,
    tabVisibilityConditions: { 'language-section': false },
    isSubmitting: false,
    availableLanguagesPerMarket: {},
  }

  constructor() {
    super()

    this.onCountryChange = this.onCountryChange.bind(this)
    this.onLanguageChange = this.onLanguageChange.bind(this)
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbLanguageSwitcherComponent.observedAttributes)
    this.waitForLocalizationForm().then(() => {
      this.findElements()
      this.addEventListeners()
      this.checkInitialState()
    })
  }

  protected waitForLocalizationForm(): Promise<HTMLFormElement> {
    return new Promise(resolve => {
      // Check if the form already exists
      const form = this.querySelector('#localization_form')
      if (form) {
        resolve(form as HTMLFormElement)
        return
      }

      // Set up an observer to watch for the form to be added
      const observer = new MutationObserver((_mutations, obs) => {
        const form = this.querySelector('#localization_form')
        if (form) {
          obs.disconnect()
          resolve(form as HTMLFormElement)
        }
      })

      // Start observing changes to the component's children
      observer.observe(this, { childList: true, subtree: true })

      // Timeout after 5 seconds to avoid hanging indefinitely
      setTimeout(() => {
        observer.disconnect()
        const form = this.querySelector('#localization_form')
        if (form) {
          resolve(form as HTMLFormElement)
        } else {
          console.warn('Localization form not found after timeout')
          resolve(null as unknown as HTMLFormElement)
        }
      }, 2000)
    })
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected findElements() {
    // The querySelector remains the same as it targets the nested form
    this.formEl =
      this.closest('form') || document.querySelector('#localization_form') // ToDo: wait from <localization-form> to be ready
    this.countrySectionEl = this.querySelector('.country-section')
    this.countryInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="country_code"]',
    )
    this.countryLabelEl = this.querySelectorAll<HTMLInputElement>(
      'label[for*="country"]',
    )
    this.languageSectionEl = this.querySelector('.language-section')
    this.languageInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="language_code"]',
    )
    this.languageLabelEl = this.querySelectorAll<HTMLInputElement>(
      'label[for*="language"]',
    )
    this.selectedLanguageSpan = this.querySelector('.selectedLanguage')
    this.redirectMessageEl = this.querySelector('.redirect-message')
    if (!this.formEl || !this.countrySectionEl || !this.languageSectionEl) {
      console.error('Language switcher child elements not found.', {
        form: this.formEl,
        countrySection: this.countrySectionEl,
        languageSection: this.languageSectionEl,
      })
    }
  }

  protected checkInitialState() {
    if (!this.countrySectionEl) {
      console.warn('Country section not found for initial check.')
      return
    }
    const selectedCountry =
      this.countrySectionEl.querySelector<HTMLInputElement>(
        'input[name="country_code"]:checked',
      )
    if (selectedCountry) {
      this.scope.languageSectionVisible = true

      // Find the tab component and update its visibility attribute
      const tabsEl = this.querySelector('sb-tabs') as SbTabsComponent
      if (tabsEl) {
        tabsEl.setAttribute('language-section-visible', 'true')
        if (tabsEl.showByName) {
          tabsEl.showByName('language-section')
        }
      }
    } else {
      this.scope.languageSectionVisible = false
    }
  }

  onCountryChange(event: Event) {
    let input: HTMLInputElement | null = null

    // If the event target is an input, use it directly
    if (
      event.target instanceof HTMLInputElement &&
      event.target.name === 'country_code'
    ) {
      input = event.target
    } else if (event.target instanceof HTMLLabelElement) {
      // If the event target is a label, get the 'for' attribute and find the input
      const forAttr = event.target.getAttribute('for')
      if (forAttr) {
        input =
          this.countrySectionEl?.querySelector<HTMLInputElement>(
            `#${forAttr}`,
          ) || null
      }
    }

    // Fallback: if input is still not found, get the checked input after a short delay
    if (!input) {
      setTimeout(() => {
        const checkedInput =
          this.countrySectionEl?.querySelector<HTMLInputElement>(
            'input[name="country_code"]:checked',
          )
        if (checkedInput) {
          this.setCountrySelection(checkedInput)
        }
      }, 0)
      return
    }

    this.setCountrySelection(input)
  }

  private updateLanguageOptions(availableLanguages: LanguageObject[]) {
    const languageContainer = this.querySelector('#language-options-container')
    if (!languageContainer) return

    // Clear previous language options
    languageContainer.innerHTML = ''

    // Add new language options based on the available languages
    availableLanguages.forEach(language => {
      const languageOption = document.createElement('div')
      languageOption.className = 'country-language-selector__option-item'
      languageOption.innerHTML = `
      <input
        type='radio'
        name='language_code'
        id='language-${language.shop_locale.iso_code}'
        value='${language.shop_locale.iso_code}'
        class='hidden peer/language'
      >
      <label
        for='language-${language.shop_locale.iso_code}'
        class='country-language-selector__option-label transition-colors bg-grey-50 hover:bg-grey-100 active:bg-grey-300 peer-checked/language:bg-celtic-green peer-checked/language:hover:bg-celtic-green'
      >
        <span class='country-language-selector__option-text peer-checked/language:text-white'>
          ${language.shop_locale.endonym_name}
        </span>
      </label>
      <span
        class='country-language-selector__check-icon hidden peer-checked/language:inline'
      >
        <svg class="h-4 w-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `

      languageContainer.appendChild(languageOption)
    })

    // Rebind event listeners to the new language options
    this.languageInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="language_code"]',
    )
    if (this.languageInputEl && this.languageLabelEl) {
      this.languageInputEl.forEach(input => {
        input.removeEventListener('change', this.onLanguageChange)
        input.addEventListener('change', this.onLanguageChange)
      })

      this.languageLabelEl.forEach(label => {
        label.addEventListener('click', this.onLanguageChange)
      })
    }
  }

  private setCountrySelection(input: HTMLInputElement) {
    const countryValue = input.value
    let labelName = ''

    // For regular countries, find the label assigned to this input
    const label = this.countrySectionEl?.querySelector(
      `label[for="country-${countryValue}"]`,
    )
    if (label) {
      // Use the label's text content or a specific child span if needed
      const textSpan = label.querySelector(
        '.country-language-selector__option-text',
      )
      labelName =
        textSpan && textSpan.textContent
          ? textSpan.textContent.trim()
          : label.textContent?.trim() || ''
    }

    // Store the values for later use if needed
    this.selectedCountryName = labelName || countryValue

    // Get the available languages for this country
    let availableLanguagesArray: LanguageObject[] = []
    try {
      const availableLanguagesObj = input.dataset.availableLanguages
        ? JSON.parse(input.dataset.availableLanguages)
        : {}

      // Convert the object to an array of language objects
      availableLanguagesArray = Object.values(availableLanguagesObj).map(
        (lang): LanguageObject => ({
          shop_locale: lang as ShopLocale,
        }),
      )
    } catch (error) {
      console.error('Error parsing available languages:', error)
      availableLanguagesArray = []
    }

    // Update the language options
    this.updateLanguageOptions(availableLanguagesArray)
    this.scope.languageSectionVisible = true
    this.scope.tabVisibilityConditions = { 'language-section': true }

    const tabsEl = this.querySelector('sb-tabs') as SbTabsComponent
    if (tabsEl) {
      tabsEl.setAttribute('language-section-visible', 'true')
      setTimeout(() => {
        if (tabsEl.showByName) {
          tabsEl.showByName('language-section')
        }
      }, 50)
    }
  }

  onLanguageChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.name === 'language_code' && target.checked && this.formEl) {
      // Store the selected language name
      const label = document.querySelector(
        `label[for="language-${target.value}"]`,
      )
      if (label) {
        const textSpan = label.querySelector(
          '.country-language-selector__option-text',
        )
        this.selectedLanguageName =
          textSpan && textSpan.textContent
            ? textSpan.textContent.trim()
            : target.value
      } else {
        this.selectedLanguageName = target.value
      }
      this.scope.isSubmitting = true

      if (this.selectedLanguageSpan) {
        this.selectedLanguageSpan.textContent = `(${this.selectedCountryName} / ${this.selectedLanguageName})`
      }

      if (this.redirectMessageEl) {
        this.redirectMessageEl.classList.remove('hidden')
      }
      // Small delay to ensure the message is visible before submitting
      setTimeout(() => {
        this.formEl?.submit()
      }, 200)
    }
  }

  protected addEventListeners() {
    this.findElements() // Ensure elements are found before bindings

    if (this.countryInputEl && this.countryLabelEl) {
      this.countryInputEl.forEach(input => {
        input.addEventListener('change', this.onCountryChange)
      })
      this.countryLabelEl.forEach(label => {
        label.addEventListener('click', this.onCountryChange)
      })
    }

    if (this.languageInputEl && this.languageLabelEl) {
      this.languageInputEl.forEach(input => {
        input.addEventListener('change', this.onLanguageChange)
      })
      this.languageLabelEl.forEach(label => {
        label.addEventListener('click', this.onLanguageChange)
      })
    }
  }

  protected removeEventListeners() {
    if (this.countryInputEl && this.countryLabelEl) {
      this.countryInputEl.forEach(input => {
        input.removeEventListener('change', this.onCountryChange)
      })
      this.countryLabelEl.forEach(label => {
        label.removeEventListener('click', this.onCountryChange)
      })
    }

    if (this.languageInputEl && this.languageLabelEl) {
      this.languageInputEl.forEach(input => {
        input.removeEventListener('change', this.onLanguageChange)
      })
      this.languageLabelEl.forEach(label => {
        label.removeEventListener('click', this.onLanguageChange)
      })
    }
  }

  async afterBind() {
    await super.afterBind()
  }

  protected requiredAttributes(): string[] {
    return []
  }

  protected template(): ReturnType<any> | null {
    // Don't render the component template - use the existing light DOM
    if (hasChildNodesTrim(this)) {
      return null
    } else {
      console.warn('Language switcher requires child elements (the form).')
      return '<div>Error: Missing form content.</div>'
    }
  }
}
